import { useContext, useRef, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { ModalContext } from 'cat-ecommerce-alloy';
import {
  ADDRESS_TYPE_BILLING,
  ADDRESS_TYPE_SHIPPING,
  PAGE_BREAKPOINTS
} from '@app/constants/commonConstants';
import { ERROR_PATH } from '@app/constants/errorConstants';
import { saveAddress, verifyAddress } from '@app/store/checkout/actions';
import {
  saveAddress as sAddress,
  verifyAddress as vAddress
} from '@app/store/addressBook/actions';
import AVIModal from '../AVIModal';
import { isAddressEquals } from '@app/store/checkout/utils';
import { PCC_PS_BILLING_SECTION_DRAWERS } from '@app/constants/featureFlags';
import { useBreakpoint } from '@app/hooks';

const getActions = isCheckout =>
  isCheckout
    ? { saveAddress, verifyAddress }
    : { saveAddress: sAddress, verifyAddress: vAddress };

const useSaveAddress = ({
  addressType,
  callbacks,
  backupErrorPath,
  addressDomain = 'checkout', //other option is AddressBook redux domain which is = 'addresses'
  isSpecialValidation = false
}) => {
  const [t] = useTranslation();
  const isMD = useBreakpoint(PAGE_BREAKPOINTS.MD);
  const billingSectionDrawerFlag = useSelector(
    s => s.featureFlag?.[PCC_PS_BILLING_SECTION_DRAWERS]
  );
  const [aviNotNeeded, setAviNotNeeded] = useState(false);
  const [isAVIMobileOpen, setIsAVIMobileOpen] = useState(false);
  const aviLabel = t('AVI_LABEL');
  const dispatch = useDispatch();
  const { addModal, closeAllModals, modals } = useContext(ModalContext);

  const addresses = useSelector(state => state[addressDomain]?.addresses) ?? {};
  const avi = useSelector(state => state[addressDomain]?.avi);
  const locale = useSelector(state => state.common?.locale);
  const isCheckoutDomain = addressDomain === 'checkout'; //this will check if the hook will be using Checkout/AddressBook redux domain

  const countries = useSelector(state => state?.dealer?.countryInfo);

  const callbacksRef = useRef(callbacks);
  const addressesRef = useRef(addresses);
  const isModalOpenRef = useRef(modals?.length > 0);
  const alternateAddressType = isCheckoutDomain
    ? addressType
    : addressesRef?.current?.new?.addressType;

  const onAddressFormSubmit = (
    newAddress,
    handleSuccessTracking,
    handleFailTracking
  ) => {
    const { country = '', state = '', ...formState } = newAddress;
    // Attempt to find a country code, or default to whatever the display name is if one is not found
    const { code: countryCode, states = [] } = countries.find(
      ({ displayName }) => displayName === country
    ) || { code: country };
    // Attempt to find a state code, or default to whatever the display name is if one is not found
    const { code: stateCode } = states.find(
      ({ displayName }) => displayName === state
    ) || { code: state };

    return dispatch(
      getActions(isCheckoutDomain).verifyAddress({
        ...formState,
        country: countryCode,
        handleSuccessTracking,
        handleFailTracking,
        state: stateCode,
        isSpecialValidation
      })
    );
  };

  const onAVIContinue = selectedAddress => {
    const { isSuggestedAddressSelected, ...selectedAddressInfo } =
      selectedAddress;
    const { ...aviAddress } = avi || {};
    return dispatch(
      getActions(isCheckoutDomain).saveAddress({
        addAddressType: alternateAddressType,
        aviAddress: isSuggestedAddressSelected ? aviAddress : undefined,
        aviOptionSelected: selectedAddressInfo,
        errorPath: ERROR_PATH.MODAL,
        onFail: callbacksRef?.current?.onFail,
        onSuccess: callbacksRef?.current?.onSuccess,
        isSpecialValidation
      })
    );
  };

  useEffect(() => {
    callbacksRef.current = callbacks;
    addressesRef.current = addresses;
    isModalOpenRef.current = modals?.length > 0;
  });

  useEffect(() => {
    const { enabled, required } = avi || {};
    if (avi && enabled && !isAddressEquals(addressesRef?.current?.new, avi)) {
      //when avi es enabled by the dealer...
      if (!billingSectionDrawerFlag || isMD) {
        addModal(
          <AVIModal
            addressDomain={addressDomain}
            current={addressesRef?.current?.new}
            locale={locale}
            required={required}
            suggested={avi}
            title={aviLabel}
            onCancel={callbacksRef?.current?.onCancel}
            onSubmit={(isSuggested, selected) =>
              onAVIContinue({
                isSuggestedAddressSelected: isSuggested,
                ...selected
              })
            }
          />,
          aviLabel,
          true
        );
      } else {
        setIsAVIMobileOpen(true);
      }
    } else if (
      (avi && !enabled) ||
      (avi && enabled && isAddressEquals(addressesRef?.current?.new, avi))
    ) {
      const errorPath =
        isModalOpenRef.current === true ? ERROR_PATH.MODAL : backupErrorPath;
      dispatch(
        getActions(isCheckoutDomain).saveAddress({
          addAddressType: alternateAddressType,
          aviAddress: undefined,
          errorPath: errorPath,
          onSuccess: callbacksRef?.current?.onSuccess,
          onFail: callbacksRef?.current?.onFail,
          isSpecialValidation
        })
      );
      setAviNotNeeded(true);
    } else {
      setAviNotNeeded(true);
    }
  }, [
    addModal,
    backupErrorPath,
    avi,
    aviLabel,
    dispatch,
    locale,
    addressDomain,
    alternateAddressType,
    isCheckoutDomain,
    billingSectionDrawerFlag,
    isMD,
    isSpecialValidation
  ]);

  useEffect(() => {
    if (addresses.lastSaved) {
      if (typeof callbacksRef?.current?.onAfterAddressUpdate === 'function') {
        callbacksRef.current.onAfterAddressUpdate();
      } else {
        closeAllModals();
      }
    }
  }, [addresses.lastSaved, closeAllModals]);

  return {
    onAddressFormSubmit,
    isAVIMobileOpen,
    setIsAVIMobileOpen,
    aviNotNeeded,
    setAviNotNeeded,
    onAVIContinue
  };
};

useSaveAddress.propTypes = {
  addressType: PropTypes.oneOf([ADDRESS_TYPE_BILLING, ADDRESS_TYPE_SHIPPING])
    .isRequired,
  callbacks: PropTypes.shape({
    onAfterAddressUpdate: PropTypes.func,
    onCancel: PropTypes.func,
    onFail: PropTypes.func,
    onSuccess: PropTypes.func
  }),
  backupErrorPath: PropTypes.string,
  isSpecialValidation: PropTypes.bool
};

export default useSaveAddress;
